import { api } from '../axios.helper';
import { Config, VisitTypeDto } from '@ecdlink/core';
import {
  ClientSummaryByPriority,
  CmsVisitDataInputModelInput,
  MoreInformation,
  Progress_VisitDataStatus,
  VisitData,
  VisitVideos,
  Infographics,
  DangerSignTranslation,
} from '@ecdlink/graphql';
import { HealthPromotion } from '@ecdlink/graphql';

class VisitService {
  _accessToken: string;

  constructor(accessToken: string) {
    this._accessToken = accessToken;
  }

  // TODO: add interface
  async addVisitFormData(input: CmsVisitDataInputModelInput): Promise<any> {
    const apiInstance = api(Config.graphQlApi, this._accessToken);
    const response = await apiInstance.post<any>(``, {
      id: 'AddVisitData',
      variables: {
        input: input,
      },
    });

    if (response.status !== 200 || response.data.errors) {
      throw new Error('Adding visit data failed - Server connection error');
    }

    return response.data.data.createInfant;
  }

  async getMoreInformation(
    section: string,
    locale: string
  ): Promise<MoreInformation[]> {
    const apiInstance = api(Config.graphQlApi, this._accessToken);
    const response = await apiInstance.post<{
      data: { moreInformation: MoreInformation[] };
      errors?: {};
    }>(``, {
      id: 'GetMoreInformation',
      variables: {
        section,
        locale,
      },
    });

    if (response.status !== 200 || response.data.errors) {
      throw new Error('Get More Information Failed - Server connection error');
    }

    return response.data.data.moreInformation;
  }

  async getHealthPromotion(
    section: string,
    locale: string,
    title?: string
  ): Promise<HealthPromotion[]> {
    const apiInstance = api(Config.graphQlApi, this._accessToken);
    const response = await apiInstance.post<{
      data: { healthPromotion: HealthPromotion[] };
      errors?: {};
    }>(``, {
      id: 'GetHealthPromotion',
      variables: {
        section,
        locale,
        title,
      },
    });

    if (response.status !== 200) {
      throw new Error('Get Health Promotion Failed - Server connection error');
    }

    return response.data.data.healthPromotion;
  }

  async getVisitVideos(
    section: string,
    locale: string,
    title?: string
  ): Promise<VisitVideos[]> {
    const apiInstance = api(Config.graphQlApi, this._accessToken);
    const response = await apiInstance.post<{
      data: { visitVideos: VisitVideos[] };
      errors?: {};
    }>(``, {
      id: 'GetVisitVideos',
      variables: {
        section,
        locale,
        title,
      },
    });

    if (response.status !== 200 || response.data.errors) {
      throw new Error('Get Visit Videos Failed - Server connection error');
    }

    return response.data.data.visitVideos;
  }

  async getInfographics(
    section: string,
    locale: string
  ): Promise<Infographics[]> {
    const apiInstance = api(Config.graphQlApi, this._accessToken);
    const response = await apiInstance.post<{
      data: { infographics: Infographics[] };
      errors?: {};
    }>(``, {
      id: 'GetInfographics',
      variables: {
        section,
        locale,
      },
    });

    if (response.status !== 200 || response.data.errors) {
      throw new Error('Get Infographics Failed - Server connection error');
    }

    return response.data.data.infographics;
  }

  async GetDangerSignTranslations(
    section: string,
    toTranslate: string
  ): Promise<DangerSignTranslation[]> {
    const apiInstance = api(Config.graphQlApi, this._accessToken);
    const response = await apiInstance.post<{
      data: { dangerSignTranslations: DangerSignTranslation[] };
      errors?: {};
    }>(``, {
      id: 'GetDangerSignTranslations',
      variables: {
        section,
        toTranslate,
      },
    });

    if (response.status !== 200 || response.data.errors) {
      throw new Error(
        'Get DangerSignTranslations Failed - Server connection error'
      );
    }

    return response.data.data.dangerSignTranslations;
  }

  async getCompletedVisitsForVisitId(visitId: string): Promise<string[]> {
    const apiInstance = api(Config.graphQlApi, this._accessToken);
    const response = await apiInstance.post<{
      data: { completedVisitsForVisitId: string[] };
      errors?: {};
    }>(``, {
      id: 'GetCompletedVisitsForVisitId',
      variables: {
        visitId,
      },
    });

    if (response.status !== 200 || response.data.errors) {
      throw new Error(
        'Get Completed Visits For Visit Id Failed - Server connection error'
      );
    }

    return response.data.data.completedVisitsForVisitId;
  }

  async getPreviousVisitInformationForInfant(
    visitId: string
  ): Promise<Progress_VisitDataStatus> {
    const apiInstance = api(Config.graphQlApi, this._accessToken);
    const response = await apiInstance.post<{
      data: { previousVisitInformationForInfant: Progress_VisitDataStatus };
      errors?: {};
    }>(``, {
      id: 'GetPreviousVisitInformationForInfant',
      variables: {
        visitId,
      },
    });

    if (response.status !== 200 || response.data.errors) {
      throw new Error(
        'Get Previous Visit Information For Infant Failed - Server connection error'
      );
    }
    return response.data.data.previousVisitInformationForInfant;
  }

  async getGrowthDataForInfant(infantId: string): Promise<VisitData[]> {
    const apiInstance = api(Config.graphQlApi, this._accessToken);
    const response = await apiInstance.post<{
      data: { growthDataForInfant: VisitData[] };
      errors?: {};
    }>(``, {
      id: 'GetGrowthDataForInfant',
      variables: {
        id: infantId,
      },
    });

    if (response.status !== 200 || response.data.errors) {
      throw new Error(
        'Get Growth Data For Infant Failed - Server connection error'
      );
    }

    return response.data.data.growthDataForInfant;
  }

  async getVisitAnswersForInfant(
    visitId: string,
    visitName: string,
    visitSection: string
  ): Promise<VisitData[]> {
    const apiInstance = api(Config.graphQlApi, this._accessToken);
    const response = await apiInstance.post<{
      data: { visitAnswersForInfant: VisitData[] };
      errors?: {};
    }>(``, {
      id: 'GetVisitAnswersForInfant',
      variables: {
        visitId,
        visitName,
        visitSection,
      },
    });

    if (response.status !== 200 || response.data.errors) {
      throw new Error('Get Visit Answers For Infant - Server connection error');
    }

    return response.data.data.visitAnswersForInfant;
  }

  async getPreviousVisitInformationForMother(
    visitId: string
  ): Promise<Progress_VisitDataStatus> {
    const apiInstance = api(Config.graphQlApi, this._accessToken);
    const response = await apiInstance.post<{
      data: { previousVisitInformationForMother: Progress_VisitDataStatus };
      errors?: {};
    }>(``, {
      id: 'GetPreviousVisitInformationForMother',
      variables: {
        visitId: visitId,
      },
    });

    if (response.status !== 200 || response.data.errors) {
      throw new Error(
        'Get Previous Visit Information For Mother Failed - Server connection error'
      );
    }

    return response.data.data.previousVisitInformationForMother;
  }

  async GetMotherSummaryByPriority(
    visitId: string
  ): Promise<ClientSummaryByPriority[]> {
    const apiInstance = api(Config.graphQlApi, this._accessToken);
    const response = await apiInstance.post<{
      data: { motherSummaryByPriority: ClientSummaryByPriority[] };
      errors?: {};
    }>(``, {
      id: 'GetMotherSummaryByPriority',
      variables: {
        visitId,
      },
    });

    if (response.status !== 200 || response.data.errors) {
      throw new Error(
        'Get client summary by priority For Mother Failed - Server connection error'
      );
    }

    return response.data.data.motherSummaryByPriority;
  }

  async GetInfantSummaryByPriority(
    visitId: string
  ): Promise<ClientSummaryByPriority[]> {
    const apiInstance = api(Config.graphQlApi, this._accessToken);
    const response = await apiInstance.post<{
      data: { infantSummaryByPriority: ClientSummaryByPriority[] };
      errors?: {};
    }>(``, {
      id: 'GetInfantSummaryByPriority',
      variables: {
        visitId,
      },
    });

    if (response.status !== 200 || response.data.errors) {
      throw new Error(
        'Get client summary by priority For infant Failed - Server connection error'
      );
    }
    return response.data.data.infantSummaryByPriority;
  }

  async getVisitAnswersForMother(
    visitId: string,
    visitName: string,
    visitSection: string
  ): Promise<VisitData[]> {
    const apiInstance = api(Config.graphQlApi, this._accessToken);
    const response = await apiInstance.post<{
      data: { visitAnswersForMother: VisitData[] };
      errors?: {};
    }>(``, {
      id: 'GetVisitAnswersForMother',
      variables: {
        visitId,
        visitName,
        visitSection,
      },
    });

    if (response.status !== 200 || response.data.errors) {
      throw new Error('Get Visit Answers For Mother - Server connection error');
    }

    return response.data.data.visitAnswersForMother;
  }

  async getVisitTypes(): Promise<VisitTypeDto[]> {
    const apiInstance = api(Config.graphQlApi, this._accessToken);
    const response = await apiInstance.post<{
      data: { GetAllVisitType: VisitTypeDto[] };
      errors?: {};
    }>(``, {
      id: 'GetAllVisitType',
    });
    if (response.status !== 200 || response.data.errors) {
      throw new Error('Get Visit Types - Server connection error');
    }
    return response.data.data.GetAllVisitType;
  }
}

export default VisitService;
